import { useEffect, useRef } from "react";

interface AgentMentionPromptProps {
  busy: boolean;
  error: string | null;
  onAdd: () => void;
  onSend: () => void;
  onCancel: () => void;
}

const baseButton = {
  height: "var(--chat-inline-edit-action-row-height)",
  padding: "0 var(--chat-space-3)",
  borderRadius: "var(--chat-radius-sm)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-sm-size)",
  fontWeight: 500,
};

export function AgentMentionPrompt({
  busy,
  error,
  onAdd,
  onSend,
  onCancel,
}: AgentMentionPromptProps) {
  const addButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    addButtonRef.current?.focus();
  }, []);

  return (
    <div
      role="group"
      aria-label="Your agent isn't in this channel"
      onKeyDown={(event) => {
        if (event.key === "Escape") onCancel();
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--chat-space-2)",
        paddingLeft: "var(--chat-composer-padding)",
        paddingRight: "var(--chat-composer-padding)",
        color: "var(--chat-fg-secondary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "var(--chat-space-2)",
        }}
      >
        <span>Your agent isn't in this channel.</span>
        <button
          ref={addButtonRef}
          type="button"
          className="chat-button"
          data-testid="agent-mention-add"
          disabled={busy}
          onClick={onAdd}
          style={{
            ...baseButton,
            borderRadius: "var(--chat-radius-lg)",
            border: "none",
            background: busy ? "transparent" : "var(--chat-cta-bg)",
            color: busy ? "var(--chat-fg-muted)" : "var(--chat-cta-fg)",
            cursor: busy ? "not-allowed" : "pointer",
          }}
        >
          {busy ? "Adding…" : "Add and send"}
        </button>
        <button
          type="button"
          className="chat-button"
          data-testid="agent-mention-send"
          disabled={busy}
          onClick={onSend}
          style={{
            ...baseButton,
            border: "none",
            background: "transparent",
            color: "var(--chat-fg-secondary)",
            cursor: busy ? "not-allowed" : "pointer",
          }}
        >
          Send without adding
        </button>
      </div>
      {error ? <div style={{ color: "var(--chat-state-danger)" }}>{error}</div> : null}
    </div>
  );
}
